// Tracker → Internal Audit view (loaded only when the switch is clicked). One row per internal audit cycle:
// client, standard(s), owner, last internal audit, reminders sent, form, quote, job. Plain — something is only
// highlighted when action is needed (red "No owner" / "No email").
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { standardsLabel, iaJobStatus, sgToday } from '../lib/service'
import { iaAction } from '../lib/iaReport'
import { toast, failed } from '../lib/toast'

const d = iso => { if (!iso) return ''; const [y, m, dd] = String(iso).slice(0, 10).split('-').map(Number); return new Date(y, m - 1, dd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) }
const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2)) + Date.now().toString(36)
const MS = [['6month', '6m'], ['9month', '9m'], ['11month', '11m']]
const red = { color: '#a32020', fontWeight: 600 }

export default function IATracker() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [search, setSearch] = useState('')
  const [showStopped, setShowStopped] = useState(false)

  async function load() {
    const [cy, rem, qs, js, meta] = await Promise.all([
      supabase.from('client_ia_cycles').select('*, clients(id, name, contact_email, ia_contact_email)'),
      supabase.from('ia_reminders_sent').select('cycle_id, last_ia_date, milestone, sent_at'),
      supabase.from('quotations').select('id, quote_ref, status, created_at, ia_cycle_id').not('ia_cycle_id', 'is', null),
      supabase.from('jobs').select('id, status, ia_stage, audit_date, ia_plan_token, ia_report_sent_at, ia_cycle_id, created_at').not('ia_cycle_id', 'is', null),
      supabase.from('pipeline_meta').select('key, owner'),
    ])
    if (cy.error) { setData({ error: cy.error.message }); return }
    const owner = Object.fromEntries((meta.data || []).map(m => [m.key, m.owner]))
    const latest = (list, cid) => (list || []).filter(x => x.ia_cycle_id === cid).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0] || null
    const rows = (cy.data || []).map(c => ({
      c, client: c.clients?.name || '—', email: c.clients?.ia_contact_email || c.clients?.contact_email, owner: owner[`C:${c.client_id}`] || '',
      sent: (rem.data || []).filter(r => r.cycle_id === c.id && r.last_ia_date === c.last_ia_date),
      quote: latest(qs.data, c.id), job: latest(js.data, c.id),
    }))
    setData({ rows })
  }
  useEffect(() => { load() }, [])

  async function copyLink(c) {
    const token = newToken()
    const { error } = await supabase.from('ia_form_links').insert({ token, cycle_id: c.id, source: 'manual' })
    if (error) return failed("Couldn't create the link", error.message)
    const url = `${window.location.origin}/ia/${token}`
    try { await navigator.clipboard.writeText(url); toast('Form link copied', 'success') } catch { window.prompt('Copy this link:', url) }
  }
  async function stop(c, name) {
    if (!window.confirm(`Stop internal audit reminders for ${name} (${standardsLabel(c.standards)})?`)) return
    try { await iaAction({ action: 'stop-cycle', cycleId: c.id }); load() } catch (e) { failed("Couldn't stop reminders", e.message) }
  }

  if (!data) return <div style={{ color: 'var(--text3)' }}>Loading…</div>
  if (data.error) return <div className="card" style={{ fontSize: 13, color: 'var(--text3)' }}>Internal Audit is not set up yet ({data.error}).</div>

  let rows = data.rows.filter(r => showStopped ? !r.c.active : r.c.active)
  if (search) rows = rows.filter(r => r.client.toLowerCase().includes(search.toLowerCase()))
  rows.sort((a, b) => (a.c.last_ia_date || '9999').localeCompare(b.c.last_ia_date || '9999') || a.client.localeCompare(b.client))
  const GRID = '1.3fr 1.1fr 0.8fr 0.7fr 0.9fr 0.7fr 1fr 1.1fr 170px'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button className={!showStopped ? 'primary sm' : 'sm'} onClick={() => setShowStopped(false)}>Active</button>
        <button className={showStopped ? 'primary sm' : 'sm'} onClick={() => setShowStopped(true)}>Reminders stopped</button>
        <input placeholder="Search client…" value={search} onChange={e => setSearch(e.target.value)} style={{ marginLeft: 'auto', width: 200 }} />
      </div>
      {rows.length === 0 && <div className="card" style={{ color: 'var(--text3)', fontSize: 13 }}>Nothing here. Internal audits are added in each client’s record.</div>}
      {rows.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="pipe-scroll">
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '6px 14px', fontSize: 13, color: 'var(--text3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>
              <div>Client</div><div>Standard(s)</div><div>Owner</div><div>Last IA</div><div>Reminders</div><div>Form</div><div>Quote</div><div>Job</div><div />
            </div>
            {rows.map(r => (
              <div key={r.c.id} style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 13 }}>
                <div style={{ fontWeight: 500 }}>{r.client}{!r.email && <div style={{ ...red, fontSize: 13 }}>No email</div>}</div>
                <div>{standardsLabel(r.c.standards) || '—'}
                  {(r.c.contract_audits_left || 0) > 0 && (!r.c.contract_until || r.c.contract_until >= sgToday()) &&
                    <div style={{ fontSize: 13, color: 'var(--brand-dark)' }}>Contract{r.c.contract_until ? ` until ${d(r.c.contract_until)}` : ''} · {r.c.contract_audits_left} left</div>}
                </div>
                <div>{r.owner || <span style={red}>No owner</span>}</div>
                <div>{d(r.c.last_ia_date) || '—'}</div>
                <div>{MS.map(([k, l]) => { const s = r.sent.find(x => x.milestone === k); return s ? <div key={k}>{l} · {d(s.sent_at)}</div> : null })}</div>
                <div>{r.c.form_submitted_at ? d(r.c.form_submitted_at) : ''}</div>
                <div>{r.quote ? <>{r.quote.quote_ref || 'Quote'} · {r.quote.status}</> : ''}</div>
                <div>{r.job ? <span style={{ color: 'var(--brand)', cursor: 'pointer' }} onClick={() => navigate(`/jobs/${r.job.id}`)}>
                  {iaJobStatus(r.job)}{r.job.ia_stage === 'awaiting_closeout' && r.job.ia_report_sent_at ? ` · ${Math.floor((Date.now() - new Date(r.job.ia_report_sent_at)) / 86400000)} days` : ''}{r.job.audit_date && !r.job.ia_report_sent_at ? ` · ${d(r.job.audit_date)}` : ''}</span> : ''}</div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {r.c.active && <button className="sm" onClick={() => copyLink(r.c)}>Copy form link</button>}
                  {r.c.active && <button className="sm" onClick={() => stop(r.c, r.client)}>Stop</button>}
                </div>
              </div>))}
          </div>
        </div>)}
    </div>
  )
}
